import { LinearProgress } from '@mui/material';
import { MonochromeButton } from 'apps/wrapper/src/components/shared/buttons/buttons.styles';
import { useSearch } from 'apps/wrapper/src/hooks/use-search/use-search';
import { useViewMode } from 'apps/wrapper/src/hooks/use-view-mode';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { HorizontalSeparator } from '../search-bar.styles';
import { EmptySearchResultsItem } from './empty-search-results-item';
import { SearchResultsFilter } from './filter/search-results-filter';
import { SearchResultsItem } from './search-results-item';
import { SearchResultsPopover } from './search-results-popover';
import { ScrollWrapper, SearchResultsWrapper } from './search-results.styles';

export interface SearchResultsProps {
  open: boolean;
  anchorEl: Element | null;
  onClose: () => void;
}

export const SearchResults: FC<SearchResultsProps> = (props) => {
  const { open, anchorEl, onClose } = props;
  const { viewMode } = useViewMode();
  const { isLoading, data, hasMore, loadMore, query } = useSearch();
  const [isShowAll, setIsShowAll] = useState(viewMode === 'Phone');
  const isPhone = viewMode === 'Phone';
  const isTablet = viewMode === 'Tablet';

  const contacts = useMemo(() => {
    if (!data) return [];
    if (isShowAll || isPhone || isTablet) return data;
    return data.slice(0, 5);
  }, [data, isShowAll, isPhone, isTablet]);

  useEffect(() => {
    setIsShowAll(isPhone);
  }, [isPhone]);

  useEffect(() => {
    if (query.length === 0) setIsShowAll(false);
  }, [query]);

  const handleLoadMore = useCallback(() => {
    setIsShowAll(true);
    loadMore();
  }, [loadMore]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      if (hasMore && isShowAll) {
        const target = e.target as HTMLDivElement;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 300) {
          loadMore();
        }
      }
    },
    [loadMore, isShowAll, hasMore]
  );

  return (
    <SearchResultsPopover open={open} anchorEl={anchorEl} onClose={onClose}>
      <SearchResultsWrapper>
        <SearchResultsFilter />

        {!isLoading && <HorizontalSeparator sx={{ margin: '0 20px 0 20px' }} />}
        {isLoading && <LinearProgress sx={{ height: '1px', margin: '0 20px 0 20px' }} />}

        <ScrollWrapper isPhone={isPhone}>
          <Scrollbars
            autoHide
            autoHeight
            onScroll={handleScroll}
            autoHeightMin={0}
            autoHeightMax={isPhone ? '75vh' : '70vh'}
            renderThumbHorizontal={(props) => <div {...props} style={{ display: 'none' }} />}
          >
            {contacts.map((contact, index) => {
              return (
                <>
                  <SearchResultsItem key={index} contact={contact} onClose={onClose} />
                  {index < contacts.length - 1 && <HorizontalSeparator sx={{ margin: '0 20px' }} />}
                </>
              );
            })}
            {contacts.length === 0 && !isLoading && <EmptySearchResultsItem />}
          </Scrollbars>
        </ScrollWrapper>

        {!isPhone && !isShowAll && !isLoading && data && data.length > 5 && (
          <MonochromeButton
            variant="outlined"
            color="info"
            size="small"
            onClick={handleLoadMore}
            sx={{ margin: '20px auto 0 auto' }}
          >
            Show More
          </MonochromeButton>
        )}
      </SearchResultsWrapper>
    </SearchResultsPopover>
  );
};
